import React, { useEffect, useState } from "react";
import CalibrationApi from "../../../api/CalibrationApi";
import { ECarrier } from "../../../Model/ECarrier";
import { EFootprint } from "../../../Model/PlateType";
import {
  Ri360HotelCalibration,
  Ri360CarrierCalibration,
  SlotSelection,
  Ri1000CarrierCalibration,
  Ri1000HotelCalibration,
  CarrierCalibration,
  HotelCalibration,
} from "../../../Model/StorageConfig";
import { EArmType, ModuleWaypoint, Waypoint, toWaypoint } from "../../../Model/Waypoint";
import WaypointUtil from "../../../Utility/WaypointUtil";
import MtiButton from "../../Buttons/MtiButton";
import SpinnerTextBox from "../../Controls/SpinnerTextBox";
import "./ArmCarrierWaypointSelector.css";
import WaypointManagerDialog from "./WaypointManagerDialog";
import CommonDropDown from "../../Common/DropDown/CommonDropDown";
import MessageContainer from "../../Common/MessageContainer/MessageContainer";
import SvgWarning from "../../../Icons/NotificationMessage/Warning";
import { useTypedSelector } from "../../../redux/reducers/RootReducer";
import { getModuleById, getModules } from "../../../redux/reducers/ModuleReducer";
import { EModuleType } from "../../../Model/EModuleType";
import { ARM_CARRIER_WAYPOINT_SELECTION } from "../../../Model/ModuleState";


interface Props {
  onSelectionUpdated: (arg: SelectionUpdatedArg) => void;
}

interface Cache {
  selectedModuleIdentifier: string | null;
  selectedArmType: EArmType | null;
  selectedCarrier: ECarrier | null;
  selectedWaypointName: string | null;
  selectedHotelNumber: number | null;
  selectedSlotNumber: number | null;
}

export interface SelectionUpdatedArg {
  selectedModuleIdentifier: string;
  selectedArmType: EArmType;
  selectedCarrier: ECarrier;
  selectedWaypoint: Waypoint;
  selectedHotelNumber: number;
  selectedSlotNumber: number;
}

function getCache(): Cache {
  let item = localStorage.getItem(ARM_CARRIER_WAYPOINT_SELECTION);
  if (item != null) {
    return JSON.parse(item);
  }

  return {
    selectedArmType: null,
    selectedCarrier: null,
    selectedModuleIdentifier: null,
    selectedHotelNumber: null,
    selectedSlotNumber: null,
    selectedWaypointName: null,
  };
}

export default function ArmCarrierWaypointSelector({ onSelectionUpdated }: Props) {
  const modules = useTypedSelector((state) => getModules(state));

  const [moduleIdentifiers, setModuleIdentifiers] = useState([] as string[]);
  const [armTypes, setArmTypes] = useState([] as EArmType[]);
  const [carriers, setCarriers] = useState([] as ECarrier[]);
  const [waypoints, setWaypoints] = useState([] as Waypoint[]);
  const [hotelWaypoints, setHotelWaypoints] = useState([] as Waypoint[]);
  const [hotels, setHotels] = useState([] as HotelCalibration[]);
  const [storageCalibrations, setStorageCalibrations] = useState([] as CarrierCalibration[]);
  const [moduleWaypoints, setModuleWaypoints] = useState([] as ModuleWaypoint[]);

  const [selectedModuleIdentifier, setSelectedModuleIdenfitier] = useState("");
  const [selectedArmType, setSelectedArmType] = useState(EArmType.ImagingArm);
  const [selectedCarrier, setSelectedCarrier] = useState(ECarrier.Unknown);
  const [selectedHotel, setSelectedHotel] = useState({} as HotelCalibration);
  const [selectedSlotNumber, setSelectedSlotNumber] = useState(1);
  const [selectedWaypoint, setSelectedWaypoint] = useState({} as Waypoint);
  const [showWaypointManagerDialog, setShowWaypointManagerDialog] = useState(false);

  const cache = getCache();

  useEffect(() => {
    CalibrationApi.getStorageCalibration().then((storageCalibrationConfig) => {
      if (storageCalibrationConfig.ri1000StorageCalibrations != null) {
        setStorageCalibrations(storageCalibrationConfig.ri1000StorageCalibrations);
      } else {
        setStorageCalibrations(storageCalibrationConfig.ri360StorageCalibrations);
      }
    });
  }, []);

  useEffect(() => {
    CalibrationApi.getWaypoint().then((moduleWaypoints) => {
      setModuleWaypoints(moduleWaypoints);
      const moduleIdentifiers = WaypointUtil.getModuleIdentifiers(moduleWaypoints);
      setModuleIdentifiers(moduleIdentifiers);
    });
  }, [storageCalibrations]);

  useEffect(() => {
    if (moduleIdentifiers.length > 0) {
      if (
        cache?.selectedModuleIdentifier != null &&
        moduleIdentifiers.includes(cache.selectedModuleIdentifier)
      ) {
        setSelectedModuleIdenfitier(cache.selectedModuleIdentifier);
        return;
      }

      setSelectedModuleIdenfitier(moduleIdentifiers[0]);
    }
  }, [moduleIdentifiers]);

  useEffect(() => {
    if (armTypes.length > 0) {
      if (cache.selectedArmType && armTypes.includes(cache.selectedArmType)) {
        setSelectedArmType(cache.selectedArmType);
        return;
      }
      setSelectedArmType(armTypes[0]);
    }
  }, [armTypes]);

  useEffect(() => {
    if (carriers.length > 0) {
      if (cache.selectedCarrier && carriers.includes(cache.selectedCarrier)) {
        setSelectedCarrier(cache.selectedCarrier);
        return;
      }
      setSelectedCarrier(carriers[0]);
    }
  }, [carriers]);

  useEffect(() => {
    if (hotels.length > 0) {
      if (
        cache.selectedHotelNumber &&
        hotels.map((h) => h.number).includes(cache.selectedHotelNumber)
      ) {
        setSelectedHotel(hotels.filter((h) => h.number === cache.selectedHotelNumber)[0]);
        setSelectedSlotNumber(cache.selectedSlotNumber ?? 1);
        return;
      }

      setSelectedHotel(hotels[0]);
    } else setSelectedHotel({} as HotelCalibration);
  }, [hotels]);

  useEffect(() => {
    if (waypoints.length > 0) {
      if (
        cache.selectedWaypointName &&
        waypoints.map((w) => w.name).includes(cache.selectedWaypointName)
      ) {
        setSelectedWaypoint(waypoints.filter((w) => w.name === cache.selectedWaypointName)[0]);
        return;
      }
      setSelectedWaypoint(waypoints[0]);
    }
  }, [waypoints]);

  useEffect(() => {
    if (selectedModuleIdentifier && selectedModuleIdentifier !== "") {
      const armTypes = WaypointUtil.getArmTypes(moduleWaypoints, selectedModuleIdentifier);

      setArmTypes(armTypes);
    }
  }, [selectedModuleIdentifier]);

  useEffect(() => {
    if (selectedModuleIdentifier === "") return;
    if (moduleWaypoints.length < 1) return;

    const carriers = WaypointUtil.getCarriers(
      moduleWaypoints,
      selectedModuleIdentifier,
      selectedArmType,
    );

    setCarriers(carriers);
  }, [selectedArmType, moduleWaypoints]);

  useEffect(() => {
    const hotels = WaypointUtil.getHotels(storageCalibrations, selectedCarrier);
    const waypoints = WaypointUtil.getWaypoints(
      moduleWaypoints,
      selectedModuleIdentifier,
      selectedArmType,
      selectedCarrier,
    );
    setWaypoints(waypoints);
    setHotels(hotels);
  }, [selectedCarrier, moduleWaypoints, selectedArmType]);

  useEffect(() => {
    if (selectedHotel.capacity > 0) {
      setSelectedSlotNumber(1);
      updateCache({...cache,
        selectedSlotNumber: 1,
      })
      updateWaypoint(selectedWaypoint.name);
    }
  }, [selectedHotel]);

  useEffect(() => {
    if (selectedWaypoint.name === undefined) return;
    if (selectedHotel.number === undefined) return;

    updateWaypoint(selectedWaypoint.name);
  }, [selectedSlotNumber]);

  useEffect(() => {
    if (!selectedWaypoint) return;
    // Fill vector name with default value first if the name from BE is null
    const newSelectedWaypoint: Waypoint = {
      ...selectedWaypoint,
      vectors: selectedWaypoint.vectors?.map((value, index) => ({...value, name: value.name ?? `Position ${index}`})),
    };
    onSelectionUpdated({
      selectedArmType: selectedArmType,
      selectedCarrier: selectedCarrier,
      selectedModuleIdentifier: selectedModuleIdentifier,
      selectedWaypoint: newSelectedWaypoint,
      selectedHotelNumber: selectedHotel.number,
      selectedSlotNumber: selectedSlotNumber,
    });
  }, [selectedWaypoint]);

  const onWaypointChanged = (waypointName: string) => {
    updateWaypoint(waypointName);
  };

  let allWp = [] as Waypoint[];
  moduleWaypoints.forEach((moduleWp) => {
    if (moduleWp.cartesianWaypoints) {
      allWp = allWp.concat(moduleWp.cartesianWaypoints.map((w) => toWaypoint(w)));
    } else if (moduleWp.cylindricalWaypoints) {
      allWp = allWp.concat(moduleWp.cylindricalWaypoints.map((w) => toWaypoint(w)));
    }
  });
  const allWaypointNames = allWp.map((x) => x.name);

  const hotelSelectionActive = hotels.length > 1;
  const slotSelectionActive = selectedHotel.capacity > 1;

  const updateWaypoint = (waypointName: string) => {
    CalibrationApi.GetWaypointForHotel(
      selectedModuleIdentifier,
      waypointName,
      selectedCarrier,
      selectedHotel.number ?? 1,
    ).then((moduleWaypoints) => {
      let hotelWaypoints: Waypoint[] = [];

      if (moduleWaypoints[0].cartesianWaypoints) {
        moduleWaypoints[0].cartesianWaypoints.forEach((w) => hotelWaypoints.push(toWaypoint(w)));
      }

      if (moduleWaypoints[0].cylindricalWaypoints) {
        moduleWaypoints[0].cylindricalWaypoints.forEach((w) => hotelWaypoints.push(toWaypoint(w)));
      }

      setHotelWaypoints(hotelWaypoints);
      setSelectedWaypoint(hotelWaypoints[selectedSlotNumber - 1]);
    });
  };

  const isPsr =
    modules.find((module) => module.identifier === selectedModuleIdentifier)?.moduleType ===
    EModuleType.PSR;

  return (
    <>
      <div className="vector-editor-selection-container">
        <div className="item">
          <label className="select-label">Module</label>
          <CommonDropDown
            id="vector-editor-module-selector"
            value={selectedModuleIdentifier}
            style={{
              fontSize: 14,
              color: "#5C5C5C",
              borderRadius: 2,
              width: 210,
              textOverflow: "ellipsis",
              height: 32,
            }}
            onChange={(value) => {
              setSelectedModuleIdenfitier(value);
              updateCache({
                ...cache,
                selectedModuleIdentifier: value,
              });
            }}
            dropDownDisplayValues={moduleIdentifiers.map((moduleIdentifier) => ({
              display: moduleIdentifier,
              value: moduleIdentifier,
              disabled: false,
            }))}
          />
        </div>

        <div className="item">
          <label className="select-label">Select Arm</label>
          <CommonDropDown
            id="vector-editor-arm-type-selector"
            value={selectedArmType}
            style={{
              fontSize: 14,
              color: "#5C5C5C",
              borderRadius: 2,
              width: 210,
              textOverflow: "ellipsis",
              height: 32,
            }}
            onChange={(value) => {
              setSelectedArmType(value);
              updateCache({
                ...cache,
                selectedArmType: value,
              });
            }}
            dropDownDisplayValues={armTypes.map((armType) => ({
              display: EArmType[armType],
              value: armType,
              disabled: false,
            }))}
          />
        </div>

        <div className="item">
          <label className="select-label">Select Carrier</label>
          <div className="select-vertical-label">
            <label>Carrier Type</label>
            <CommonDropDown
              id="vector-editor-carrier-type-selector"
              value={selectedCarrier}
              style={{
                fontSize: 14,
                color: "#5C5C5C",
                borderRadius: 2,
                width: 210,
                textOverflow: "ellipsis",
                height: 32,
              }}
              onChange={(value) => {
                setSelectedCarrier(value);
                updateCache({
                  ...cache,
                  selectedCarrier: value,
                });
              }}
              dropDownDisplayValues={carriers.map((carrier) => ({
                display: ECarrier[carrier],
                value: carrier,
                disabled: false,
              }))}
            />
          </div>

          {(hotelSelectionActive || slotSelectionActive) && <div className="vertical-hair"></div>}

          {hotelSelectionActive && (
            <div className="select-vertical-label" style={{ marginRight: 24 }}>
              <label>Hotel</label>
              <CommonDropDown
                id="vector-editor-hotel-selector"
                value={selectedHotel ? selectedHotel.number : 0}
                style={{
                  fontSize: 14,
                  color: "#5C5C5C",
                  borderRadius: 2,
                  width: 141,
                  textOverflow: "ellipsis",
                  height: 32,
                }}
                onChange={(value) => {
                  const hotelNumber = value;
                  setSelectedHotel(hotels.filter((h) => h.number == hotelNumber)[0]);
                  updateCache({
                    ...cache,
                    selectedHotelNumber: value,
                  });
                }}
                dropDownDisplayValues={hotels.map((hotel) => ({
                  display: `Hotel ${EFootprint[hotel.footprint]} ${hotel.number}`,
                  value: hotel.number,
                  disabled: false,
                }))}
              />
            </div>
          )}

          {slotSelectionActive && (
            <div className="select-vertical-label">
              <label>Slot</label>
              <SpinnerTextBox
                disabled={selectedHotel.capacity < 2}
                style={{
                  width: "68px",
                  borderRadius: 2,
                }}
                value={selectedSlotNumber}
                onValueChanged={(value) => {
                  setSelectedSlotNumber(value);
                  updateCache({
                    ...cache,
                    selectedSlotNumber: value,
                  });
                }}
                step={1}
                min={1}
                max={selectedHotel.capacity}
              />
            </div>
          )}
        </div>

        <div className="item">
          <label className="select-label">Select Vector</label>
          <CommonDropDown
            id="vector-editor-vector-selector"
            value={selectedWaypoint ? selectedWaypoint.name : ""}
            style={{
              fontSize: 14,
              color: "#5C5C5C",
              borderRadius: 2,
              width: 210,
              textOverflow: "ellipsis",
              height: 32,
            }}
            onChange={(value) => {
              onWaypointChanged(value);
              updateCache({
                ...cache,
                selectedWaypointName: value,
              });
            }}
            dropDownDisplayValues={waypoints.map((waypoint) => ({
              display: waypoint.name,
              value: waypoint.name,
              disabled: false,
            }))}
          />
          <MtiButton
            onClick={() => {
              setShowWaypointManagerDialog(true);
            }}
            style={{
              alignSelf: "flex-end",
              display: "flex",
              alignItems: "center",
              minHeight: 32,
              minWidth: 32,
              fontSize: 16,
              marginLeft: 16,
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12.75 9C12.75 11.0711 11.0711 12.75 9.00001 12.75C6.92894 12.75 5.25001 11.0711 5.25001 9C5.25001 6.92893 6.92894 5.25 9.00001 5.25C11.0711 5.25 12.75 6.92893 12.75 9ZM11.25 9C11.25 10.2426 10.2426 11.25 9.00001 11.25C7.75737 11.25 6.75001 10.2426 6.75001 9C6.75001 7.75736 7.75737 6.75 9.00001 6.75C10.2426 6.75 11.25 7.75736 11.25 9Z"
                fill="white"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M15.6771 8.00475L17.6804 6.61433C17.4907 5.92255 17.2205 5.26405 16.8806 4.64953L16.7163 4.36498C16.3464 3.75051 15.9052 3.18384 15.4041 2.67642L13.1999 3.71537C12.682 3.30325 12.1021 2.96565 11.4766 2.7188L11.2742 0.289794C10.5478 0.100663 9.78564 0 9.00001 0C8.34813 0 7.71243 0.0693061 7.09989 0.200952L6.79784 2.61739C6.06699 2.8695 5.39299 3.2436 4.80017 3.71536L2.59593 2.67641C2.09494 3.18374 1.65379 3.75029 1.28394 4.36463L1.11925 4.64989C0.840291 5.15418 0.608333 5.68808 0.429282 6.24568L2.37193 7.71623C2.29191 8.13184 2.25001 8.56103 2.25001 9C2.25001 9.33814 2.27487 9.67047 2.32287 9.99527L0.319611 11.3857C0.509282 12.0774 0.779418 12.7358 1.11933 13.3503L1.28386 13.6352C1.58725 14.1392 1.93862 14.611 2.33165 15.0443L4.57539 14.0977C5.14821 14.5953 5.80574 14.998 6.52345 15.2812L6.72587 17.7102C7.45228 17.8993 8.21439 18 9.00001 18C9.78564 18 10.5478 17.8993 11.2742 17.7102L11.4766 15.2812C12.1021 15.0343 12.682 14.6968 13.1999 14.2846L15.4041 15.3236C15.9052 14.8161 16.3465 14.2494 16.7164 13.6348L16.8805 13.3507C17.2205 12.7361 17.4907 12.0775 17.6804 11.3857L15.6771 9.99526C15.7251 9.67046 15.75 9.33813 15.75 9C15.75 8.66187 15.7251 8.32954 15.6771 8.00475ZM13.5 9C13.5 11.4853 11.4853 13.5 9.00001 13.5C6.51473 13.5 4.50001 11.4853 4.50001 9C4.50001 6.51472 6.51473 4.5 9.00001 4.5C11.4853 4.5 13.5 6.51472 13.5 9Z"
                fill="white"
              />
            </svg>
          </MtiButton>
        </div>
        <WaypointManagerDialog
          slotNumber={selectedSlotNumber}
          hotelNumber={selectedHotel.number}
          moduleIdentifier={selectedModuleIdentifier}
          onWaypointUpdated={() => {
            CalibrationApi.getWaypoint().then((moduleWaypoints) => {
              setModuleWaypoints(moduleWaypoints);
            });
          }}
          onHide={() => {
            setShowWaypointManagerDialog(false);
          }}
          show={showWaypointManagerDialog}
          armTypes={armTypes}
          carriers={carriers}
          selectedArmType={selectedArmType}
          selectedCarrier={selectedCarrier}
          waypoints={waypoints}
          allWaypointNames={allWaypointNames}
        />
      </div>

      {isPsr ? (
        <div className="psr-warning-container">
          <MessageContainer
            variant="warning"
            icon={<SvgWarning />}
            message="Please make sure PSR in the safe location before run the vector to avoid system crashed "
          />
        </div>
      ) : null}
    </>
  );

  function updateCache(newCache: Cache) {
    cache.selectedModuleIdentifier = newCache.selectedModuleIdentifier;
    cache.selectedArmType = newCache.selectedArmType;
    cache.selectedCarrier = newCache.selectedCarrier;
    cache.selectedHotelNumber = newCache.selectedHotelNumber;
    cache.selectedSlotNumber = newCache.selectedSlotNumber;
    cache.selectedWaypointName = newCache.selectedWaypointName;

    localStorage.setItem(ARM_CARRIER_WAYPOINT_SELECTION, JSON.stringify(cache));
  }
}
